import {
  type MjGame,
  canMatch,
  canUndo,
  freeTiles,
  hint as findHint,
  isWon,
  layoutById,
  newMahjongSolitaire,
  removePair,
  reshuffle as reshuffleGame,
  undo as undoGame,
} from '@chessmate/game-engine';
import { create } from 'zustand';
import type { AppQuery } from '../lib/query.js';
import { playSfx, setAmbientMood } from '../services/audio.js';

/**
 * The Mahjong Solitaire store: one player, no robots. A board is dealt from a layout and a seed (always clearable); the player taps
 * two free tiles with matching faces to remove them. Its own store, like the card games'. Nothing is staked: the score is moves.
 */
export const DEFAULT_LAYOUT = 'pyramid';

/** `dead`: stuck AND a shuffle cannot help, because the tiles left can never come off as pairs (for example two stacked tiles). */
export type MjMessage = 'blocked' | 'no-match' | 'matched' | 'dead' | null;

export interface MahjongSolitaireState {
  game: MjGame | null;
  layout: string;
  seed: number | null;
  /** The first tile of a pair, ringed while the player looks for its match. */
  selected: number | null;
  /** The pair `hint` is pointing at, until the next move. */
  hintPair: readonly [number, number] | null;
  message: MjMessage;
  /** Assist (off by default): show which tiles are free. */
  assistFree: boolean;
  query: AppQuery | null;
  start(query: AppQuery, seed?: number): void;
  newGame(layout?: string): void;
  stop(): void;
  tap(id: number): void;
  hint(): void;
  undo(): void;
  reshuffle(): void;
  setAssistFree(on: boolean): void;
}

const freshSeed = () => Math.floor(Math.random() * 2 ** 31);

export const useMahjongSolitaireStore = create<MahjongSolitaireState>((set, get) => {
  const begin = (query: AppQuery, layout: string, seed: number) => {
    layoutById(layout);
    set({ query, layout, seed, game: newMahjongSolitaire(layout, seed), selected: null, hintPair: null, message: null });
  };
  return {
    game: null,
    layout: DEFAULT_LAYOUT,
    seed: null,
    selected: null,
    hintPair: null,
    message: null,
    assistFree: false,
    query: null,
    start: (query, seed) => {
      setAmbientMood('cards');
      return begin(query, get().layout, seed ?? query.deal ?? freshSeed());
    },
    newGame: (layout) => {
      const { query } = get();
      if (query) begin(query, layout ?? get().layout, query.deal ?? freshSeed());
    },
    stop: () => set({ game: null, seed: null, selected: null, hintPair: null, message: null }),
    tap: (id) => {
      playSfx('select');
      const { game, selected } = get();
      if (!game || isWon(game)) return;
      if (!freeTiles(game).includes(id)) {
        set({ message: 'blocked', selected: null });
        return;
      }
      if (selected === null) {
        set({ selected: id, message: null, hintPair: null });
        return;
      }
      if (selected === id) {
        set({ selected: null, message: null });
        return;
      }
      if (canMatch(game, selected, id)) {
        set({ game: removePair(game, selected, id), selected: null, hintPair: null, message: 'matched' });
        return;
      }
      set({ selected: id, message: 'no-match' });
    },
    hint: () => {
      const { game } = get();
      if (!game) return;
      set({ hintPair: findHint(game), selected: null, message: null });
    },
    undo: () => {
      const { game } = get();
      if (!game || !canUndo(game)) return;
      set({ game: undoGame(game), selected: null, hintPair: null, message: null });
    },
    reshuffle: () => {
      const { game } = get();
      if (!game || isWon(game)) return;
      const next = reshuffleGame(game);
      // The engine hands back the same game when no order of pair removals exists for the tiles left, whatever their faces: say so
      // instead of leaving the player with a shuffle button that does nothing.
      if (next === game) set({ selected: null, hintPair: null, message: 'dead' });
      else set({ game: next, selected: null, hintPair: null, message: null });
    },
    setAssistFree: (assistFree) => set({ assistFree }),
  };
});
